import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import {
  NON_ADMIN_ROLE,
  useAuth,
  useRolesQuery,
  useTellescopeMutation,
  useTellescopeQuery,
  useUsersQuery,
} from "@tellescope/tanstack-query";
import { type MultiSelectOption } from "@tellescope/ui";
import { formatName } from "@tellescope/utils";
import { CONTACT_DETAILS_VIEW_TYPE } from "../ContactDetailsHeader/constants";

export interface UseViewConfigFormArgs {
  viewId?: string;
  onSubmitSuccess: (viewId: string) => void;
  onSubmitError: () => void;
}

const seedCurrentUser = (
  userIds: string[],
  { visibleToAllUsers, currentUserId }: { visibleToAllUsers: boolean; currentUserId: string }
) => (visibleToAllUsers || userIds.includes(currentUserId) ? userIds : [...userIds, currentUserId]);

export const useViewConfigForm = ({ viewId, onSubmitSuccess, onSubmitError }: UseViewConfigFormArgs) => {
  const { t } = useTranslation("contacts");

  const viewQuery = useTellescopeQuery({
    model: "custom_dashboards",
    method: "getOne",
    request: viewId ?? "",
    enabled: !!viewId,
  });

  const { data: users } = useUsersQuery();
  const { currentUser } = useAuth();

  const userOptions = useMemo<MultiSelectOption[]>(
    () =>
      (users ?? []).map((user) =>
        user.id === currentUser.id
          ? { value: user.id, label: t("{{name}} (you)", { name: formatName(user) || user.id }), disabled: true }
          : { value: user.id, label: formatName(user) || user.id }
      ),
    [users, currentUser.id, t]
  );

  const { roles: allRoleOptions } = useRolesQuery({
    select: (roles) =>
      roles.map(({ role }) => ({ value: role, label: role === NON_ADMIN_ROLE ? t("Non-Admin") : role })),
  });

  const createViewMutation = useTellescopeMutation({
    model: "custom_dashboards",
    method: "createOne",
    onSuccess: ({ id }) => onSubmitSuccess(id),
    onError: onSubmitError,
  });

  const editViewMutation = useTellescopeMutation({
    model: "custom_dashboards",
    method: "updateOne",
    onSuccess: ({ id }) => onSubmitSuccess(id),
    onError: onSubmitError,
  });

  const form = useForm({
    fields: {
      title: {
        type: "text",
        initialValue: viewQuery.data?.title || "",
        required: true,
      },
      visibleToAllUsers: {
        type: "checkbox",
        initialValue: viewQuery.data?.visibleToAllUsers ?? false,
      },
      defaultForRoles: {
        type: "multiselect",
        initialValue: viewQuery.data?.defaultForRoles ?? [],
      },
      hiddenFromRoles: {
        type: "multiselect",
        initialValue: viewQuery.data?.hiddenFromRoles ?? [],
      },
      defaultForUserIds: {
        type: "multiselect",
        initialValue: viewQuery.data?.defaultForUserIds ?? [],
      },
      userIds: {
        type: "multiselect",
        initialValue: seedCurrentUser(viewQuery.data?.userIds ?? [], {
          visibleToAllUsers: viewQuery.data?.visibleToAllUsers ?? false,
          currentUserId: currentUser.id,
        }),
      },
    },
    onSubmit: (values) => {
      const sharedWithEveryone = values.visibleToAllUsers;
      const allowedUserIds = sharedWithEveryone ? undefined : values.userIds;

      const config = {
        title: values.title.trim(),
        visibleToAllUsers: sharedWithEveryone,
        defaultForRoles: values.defaultForRoles,
        hiddenFromRoles: values.hiddenFromRoles.filter((role) => !values.defaultForRoles.includes(role)),
        defaultForUserIds: allowedUserIds
          ? values.defaultForUserIds.filter((userId) => allowedUserIds.includes(userId))
          : values.defaultForUserIds,
        userIds: sharedWithEveryone ? [] : values.userIds,
      };

      if (viewId) {
        editViewMutation.mutate({ id: viewId, ...config, _options: { replaceObjectFields: true } });
        return;
      }

      createViewMutation.mutate({ ...config, type: CONTACT_DETAILS_VIEW_TYPE, blocks: [] });
    },
    validators: {
      title: (value) => (value.trim() ? undefined : t("Enter a title")),
      hiddenFromRoles: (value, allValues) =>
        value.some((role) => allValues.defaultForRoles.includes(role))
          ? t("A role cannot be both a default and hidden")
          : undefined,
      defaultForRoles: (value, allValues) =>
        value.some((role) => allValues.hiddenFromRoles.includes(role))
          ? t("A role cannot be both a default and hidden")
          : undefined,
      defaultForUserIds: (value, allValues) =>
        !allValues.visibleToAllUsers && value.some((userId) => !allValues.userIds.includes(userId))
          ? t("A view can only default for users who can see it")
          : undefined,
    },
    sideEffects: {
      visibleToAllUsers: (value, allValues) => {
        if (value) {
          return { userIds: [] };
        }

        const userIds = [currentUser.id];

        return {
          userIds,
          defaultForUserIds: allValues.defaultForUserIds.filter((userId) => userIds.includes(userId)),
        };
      },
      userIds: (value, allValues) => {
        if (allValues.visibleToAllUsers) {
          return {};
        }

        const withCurrentUser = seedCurrentUser(value, { visibleToAllUsers: false, currentUserId: currentUser.id });

        return {
          userIds: withCurrentUser,
          defaultForUserIds: allValues.defaultForUserIds.filter((userId) => withCurrentUser.includes(userId)),
        };
      },
      defaultForRoles: (value, allValues) => ({
        hiddenFromRoles: allValues.hiddenFromRoles.filter((role) => !value.includes(role)),
      }),
      hiddenFromRoles: (value, allValues) => ({
        defaultForRoles: allValues.defaultForRoles.filter((role) => !value.includes(role)),
      }),
    },
  });

  const { visibleToAllUsers, userIds, defaultForRoles, hiddenFromRoles } = form.values;

  const defaultForRoleOptions = useMemo(
    () => allRoleOptions.filter((option) => !hiddenFromRoles.includes(option.value)),
    [allRoleOptions, hiddenFromRoles]
  );
  const hiddenFromRoleOptions = useMemo(
    () => allRoleOptions.filter((option) => !defaultForRoles.includes(option.value)),
    [allRoleOptions, defaultForRoles]
  );

  const defaultForUserOptions = useMemo(
    () => (visibleToAllUsers ? userOptions : userOptions.filter((option) => userIds.includes(option.value))),
    [userOptions, userIds, visibleToAllUsers]
  );

  return {
    form,
    userOptions,
    defaultForUserOptions,
    defaultForRoleOptions,
    hiddenFromRoleOptions,
    isLoading: !!viewId && viewQuery.isLoading,
    isSaving: createViewMutation.isPending || editViewMutation.isPending,
  };
};
